import * as IoC from 'inversify';
import { TrackingPlugin, TrackingPluginSymbol } from '../../tracking-api';
import * as Resolver from './resolver';
import * as Plugin from './plugin';
import * as Local from './local';
import * as Common from '../../common';
import { Scorm2004Client, ErrorHandlingScorm2004Client, ErrorHandlingScorm2004ClientSymbol } from './client';
import { Scorm2004SuspendDataStore,
         Scorm2004ObjectiveDataStore, Scorm2004SuspendDataProtobufSerializer,
         Scorm2004InteractionDataStore } from './store';
import { questionAttemptScorm2004TrackingModules } from '@/app/question-attempt/tracking/scorm/di';
import { examScorm2004TrackingModules } from '@/app/exam/tracking/scorm/di';
import { sectionStateScorm2004TrackingModules } from '@/app/section-state/tracking/scorm2004/di';
import { StateSerializationPluginSymbol } from '@/app/state-serialization/api';
import { courseScorm2004TrackingModule } from '@/app/course/tracking/scorm2004/di';
import { badgeScorm2004TrackingModules } from '@/app/badge/tracking/scorm2004/di';
import { interactionScorm2004TrackingModules } from '@/app/interaction/tracking/scorm2004/di';
import { questionScorm2004TrackingModules } from '@/app/question/tracking/scorm2004/di';
import { bookmarkScorm2004TrackingModules } from '@/app/bookmark/tracking/scorm2004/di';
import { FunctionRetryStrategy, logaritmicRetryFunction } from '@xlearn/async-toolbox'
import { quizSessionScorm2004TrackingModules } from '@/app/quiz-session/tracking/scorm/di';
import { examSessionScorm2004TrackingModules } from '@/app/exam-session/tracking/scorm/di';
import { practiceSessionScorm2004TrackingModules } from '@/app/practice-session/tracking/scorm/di';
import { ScormErrorNotificationService, ScormErrorNotificationServiceSymbol } from './client/scorm-notification-service';
import { studentInfoScorm2004TrackingModules } from '@/app/student/tracking/scorm2004/di';

export const scorm2004Module = new IoC.ContainerModule((bind) => {

  bind(Resolver.APIFinder).toDynamicValue((_) => new Resolver.APIFinder('API_1484_11')).inSingletonScope();
  bind(Resolver.RemoteScorm2004ApiResolver).toDynamicValue(
    (c) => {
      const apiFinder = c.container.get(Resolver.APIFinder);
      return new Resolver.RemoteScorm2004ApiResolver(apiFinder);
    }).inSingletonScope();
  bind<TrackingPlugin>(TrackingPluginSymbol).toDynamicValue((c) => {
    const apiFinder = c.container.get(Resolver.APIFinder);
    const resolver = c.container.get(Resolver.RemoteScorm2004ApiResolver);
    const phaseFactory = c.container.get<Plugin.InitializedPhaseFactory>(Plugin.InitializedPhaseFactorySymbol);
    const options = {
      name: 'scorm2004',
      priority: 5,
      isAvailable: async () => apiFinder.find(window) !== null
    };
    return new Plugin.Scorm2004TrackingPlugin(resolver, phaseFactory, options);
  });

  bind(Resolver.LocalScorm2004ApiResolver).toDynamicValue(
    (_) => new Resolver.LocalScorm2004ApiResolver(new Local.LocalStorageLocalScorm2004Repository()))
    .inSingletonScope();
  bind<TrackingPlugin>(TrackingPluginSymbol).toDynamicValue((c) => {
    const resolver = c.container.get(Resolver.LocalScorm2004ApiResolver);
    const phaseFactory = c.container.get<Plugin.InitializedPhaseFactory>(Plugin.InitializedPhaseFactorySymbol);
    const options = {
      name: 'scorm2004-local',
      priority: 10,
      isAvailable: async () => true
    };
    return new Plugin.Scorm2004TrackingPlugin(resolver, phaseFactory, options);
  });

  bind<Plugin.InitializedPhase>(Plugin.InitializedPhaseFactorySymbol).toFactory((context) => {
    return (client: Scorm2004Client) => {

      const childContainer = new IoC.Container();
      childContainer.parent = context.container;

      childContainer.load(initializedPhaseModule);

      // TODO: refactor out tracking plugins
      childContainer.load(...questionAttemptScorm2004TrackingModules)
      childContainer.load(...quizSessionScorm2004TrackingModules)
//      childContainer.load(stateSerializationDomainModule)
      childContainer.load(...examScorm2004TrackingModules);
      childContainer.load(...sectionStateScorm2004TrackingModules);
      childContainer.load(courseScorm2004TrackingModule)
      childContainer.load(...badgeScorm2004TrackingModules);
      childContainer.load(...interactionScorm2004TrackingModules);
      childContainer.load(...questionScorm2004TrackingModules);
      childContainer.load(...bookmarkScorm2004TrackingModules);
      childContainer.load(...examSessionScorm2004TrackingModules);
      childContainer.load(...practiceSessionScorm2004TrackingModules);
      childContainer.load(...studentInfoScorm2004TrackingModules);

      childContainer.bind<Scorm2004Client>(Scorm2004Client).toConstantValue(client);
      childContainer.bind<ErrorHandlingScorm2004Client>(ErrorHandlingScorm2004ClientSymbol).
            toConstantValue(new ErrorHandlingScorm2004Client(
              client,
              new FunctionRetryStrategy(
                logaritmicRetryFunction({ initialDelayMs: 1000, delayAt10thRetryMs: 4000}))))
      childContainer.bind<ScormErrorNotificationService>(ScormErrorNotificationServiceSymbol)
        .to(ScormErrorNotificationService)

      return childContainer.get(Plugin.InitializedPhase);
    };
  });
});

const initializedPhaseModule = new IoC.ContainerModule((bind) => {

  // store
  bind(Common.SuspendDataStoreSymbol).to(Scorm2004SuspendDataStore).inSingletonScope();
  bind(StateSerializationPluginSymbol).to(Scorm2004SuspendDataProtobufSerializer)
  bind(Scorm2004InteractionDataStore).toSelf().inSingletonScope();
  bind(Scorm2004ObjectiveDataStore).toSelf().inSingletonScope();

  // common
  bind(Common.StoreCommandExecutor).toSelf().inSingletonScope();
  Common.registerCommandHandlerFactory(bind);
  Common.registerQueryHandlerFactory(bind);

  // phase
  bind(Plugin.InitializedPhase).toSelf();
});
